import { z } from "zod";

/**
 * Coupons and spend-and-save promos (`05-merchant-backoffice.md` §§ 6–7).
 *
 * A template is the offer; a coupon is one instance of it in one person's
 * hands. Keeping them apart is what makes "how many are still out there" a
 * question with an answer.
 */

const Money = z.string().regex(/^\d+$/, "minor units, as digits");

export const COUPON_KINDS = ["amount", "percent"] as const;
export const COUPON_STATUSES = ["issued", "used", "expired", "void"] as const;

export const CouponTemplateSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  kind: z.enum(COUPON_KINDS),
  /** Minor units for `amount`; basis points for `percent`. */
  value: z.string(),
  minSpend: Money,
  maxDiscount: Money.nullable(),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  issueLimit: z.number().int().nullable(),
  perMemberLimit: z.number().int().nullable(),
  isActive: z.boolean(),
  /** What has happened to it: issued, redeemed, and what that was worth. */
  issuedCount: z.number().int(),
  usedCount: z.number().int(),
  discountGiven: z.string(),
});

export const SaveCouponTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(COUPON_KINDS),
  value: z.string().regex(/^\d+$/),
  minSpend: Money.default("0"),
  maxDiscount: Money.nullable().default(null),
  validFrom: z.string().nullable().default(null),
  validTo: z.string().nullable().default(null),
  issueLimit: z.number().int().positive().nullable().default(null),
  perMemberLimit: z.number().int().positive().nullable().default(null),
  isActive: z.boolean().default(true),
});

/**
 * Spelled out rather than derived with `.partial()`.
 *
 * `.partial()` makes a field optional but **keeps its `.default()`**, so an
 * omitted key arrives as its default rather than as absent — and a PATCH that
 * meant "rename this" would quietly reset everything it did not mention.
 */
export const UpdateCouponTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  kind: z.enum(COUPON_KINDS).optional(),
  value: z.string().regex(/^\d+$/).optional(),
  minSpend: Money.optional(),
  maxDiscount: Money.nullable().optional(),
  validFrom: z.string().nullable().optional(),
  validTo: z.string().nullable().optional(),
  issueLimit: z.number().int().positive().nullable().optional(),
  perMemberLimit: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
});

/** Sending a coupon to a member's wallet *(verified — `manuallySendCouponByWallet`)*. */
export const IssueCouponSchema = z.object({
  consumerIds: z.array(z.uuid()).min(1).max(500),
});

export const CouponSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  templateId: z.uuid(),
  templateName: z.string(),
  status: z.enum(COUPON_STATUSES),
  consumerId: z.uuid().nullable(),
  consumerName: z.string().nullable(),
  usedOrderId: z.uuid().nullable(),
  usedAmount: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
});

export const CouponListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  size: z.coerce.number().int().positive().max(200).default(20),
  q: z.string().trim().optional(),
  status: z.enum(COUPON_STATUSES).optional(),
  templateId: z.uuid().optional(),
  consumerId: z.uuid().optional(),
});

/** Redeeming a coupon against an open bill. */
export const RedeemCouponSchema = z.object({ code: z.string().trim().min(1).max(40) });

// ── Spend-and-save promos ───────────────────────────────────────────────────

export const DiscountPromoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  threshold: Money,
  reduction: Money,
  stacks: z.boolean(),
  validFrom: z.string().nullable(),
  validTo: z.string().nullable(),
  weekdays: z.array(z.number().int()),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  isActive: z.boolean(),
  sort: z.number().int(),
});

export const SaveDiscountPromoSchema = z.object({
  name: z.string().trim().min(1).max(120),
  threshold: Money,
  reduction: Money,
  stacks: z.boolean().default(false),
  validFrom: z.string().nullable().default(null),
  validTo: z.string().nullable().default(null),
  /** 0 is Sunday. Empty means every day. */
  weekdays: z.array(z.number().int().min(0).max(6)).default([]),
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .default(null),
  endTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .default(null),
  isActive: z.boolean().default(true),
  sort: z.number().int().default(0),
});

/**
 * Spelled out rather than derived with `.partial()`.
 *
 * `.partial()` makes a field optional but **keeps its `.default()`**, so an
 * omitted key arrives as its default rather than as absent — and a PATCH that
 * meant "rename this" would quietly reset everything it did not mention.
 */
export const UpdateDiscountPromoSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  threshold: Money.optional(),
  reduction: Money.optional(),
  stacks: z.boolean().optional(),
  validFrom: z.string().nullable().optional(),
  validTo: z.string().nullable().optional(),
  weekdays: z.array(z.number().int().min(0).max(6)).optional(),
  startTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .optional(),
  endTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .nullable()
    .optional(),
  isActive: z.boolean().optional(),
  sort: z.number().int().optional(),
});

/**
 * What a bill qualifies for right now.
 *
 * Offered rather than applied: the source's promos are a cashier's decision at
 * the till *(verified — they sit beside the discount controls)*, and applying
 * money silently is how a merchant loses track of their own margin.
 */
export const PromoOfferSchema = z.object({
  promoId: z.uuid(),
  name: z.string(),
  threshold: Money,
  reduction: Money,
  stacks: z.boolean(),
});
